import { Header, useStyles } from '@archipro-design/aria';
import * as S from './HeaderSection.style';
import SubRouteNavigation from '../sub-route-navigation/SubRouteNavigation';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useMatchesHandleData, useRootData } from '@modules/root';
import { useRef, useState, useEffect } from 'react';
import { Link, useLoaderData } from '@remix-run/react';
import type { ProfessionalDetailRootLoaderData } from '../../page/ProfessionalPage';
import { superscriptize } from '~/utils';
import type { HeaderSectionProps } from './HeaderSection';
import RightHeaderActionLinkDesktop from './RightHeaderActionLink.desktop';
import { useScroll, useSize } from 'ahooks';
import {
    ENQUIRE_MODAL_SHOWUP_KEY,
    ENQUIRE_MODAL_SHOWUP_KEY_VALUE,
} from '~/modules/enquiry/constants';
import { BannerAndLogo } from '@modules/professional/component/overview-tab/banner-and-logo/BannerAndLogo';
import { BannerSegmentEnum } from 'generated/graphql';
import { Button as RoccoButton } from '@rocco/components/button';

export const HeaderSectionDesktop = ({
    openEnquiryModal,
    breadcrumbs = [],
    primaryCTA,
    banners,
}: HeaderSectionProps) => {
    const { isApStaff, country, linkedProfiles } =
        useLoaderData<ProfessionalDetailRootLoaderData>();
    const {
        Title,
        Link: professionalLink,
        LibraryLocations,
        Country,
        CompanyLogo,
        LogoBackgroundColor,
        Website,
        ID,
        Content,
    } = useProfessionalInitialRouteData();
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const hasLinkedLocation =
        linkedProfiles?.linkedProfilesList.some((p) =>
            p.LibraryLocations.some((l) => !l.LocationHidden)
        ) ?? false;

    const iconAdjustedTitle =
        superscriptize(Title) +
        (isApStaff && country !== Country ? ` (${Country})` : '');

    const innerRef = useRef<HTMLDivElement>(null);
    const innerSize = useSize(innerRef);

    const position = useScroll();

    const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);

    const { env } = useRootData();
    const url = new URL(professionalLink, `https://${env.host}`);
    url.searchParams.set(
        ENQUIRE_MODAL_SHOWUP_KEY,
        ENQUIRE_MODAL_SHOWUP_KEY_VALUE
    );
    const professionalPageWithEnquiry = url.pathname + url.search;

    const handleEnquiryModalOpenChange = (isOpen: boolean) => {
        setEnquiryModalOpen(isOpen);
    };

    const lastBreadcrumb = breadcrumbs?.[breadcrumbs.length - 1];
    const professionalCategoryLabel = lastBreadcrumb
        ? `${lastBreadcrumb?.Title}`
        : '';

    const professionalCategory = isBMPreview ? (
        <span>{professionalCategoryLabel}</span>
    ) : (
        <Link to={breadcrumbs?.[breadcrumbs.length - 1]?.Link ?? ''}>
            {professionalCategoryLabel}
        </Link>
    );

    const excludeAboutBanner = banners?.filter(
        (b) => b.bannerSegment !== BannerSegmentEnum.Aboutus
    );

    const wrapperRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [isSticky, setIsSticky] = useState(false);
    const { css } = useStyles({
        innerHeight: innerSize?.height,
        scrollY: position?.top,
        isBMPreview,
        isSticky,
    });

    useEffect(() => {
        if (!sentinelRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                // the sentinelRef triggers the event when going out of the screen while scrolling up
                // checking intersectionRect to make sure we're in the right position
                if ((entry?.intersectionRect?.x ?? 0) > 0) {
                    setIsSticky(!entry?.isIntersecting);
                }
            },
            {
                threshold: [0.01],
                rootMargin: '0px',
            }
        );

        observer.observe(sentinelRef.current);

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <>
            {excludeAboutBanner && (
                <BannerAndLogo
                    banners={excludeAboutBanner}
                    awards={[]}
                    professional={{
                        CompanyLogo: CompanyLogo ?? '',
                        LogoBackgroundColor: LogoBackgroundColor ?? '',
                        Title: Title ?? '',
                        Link: professionalLink ?? '',
                        Website: Website ?? '',
                        ID: ID ?? 0,
                        Content: Content ?? '',
                    }}
                    hideLogo={true}
                    openEnquiryModal={openEnquiryModal}
                    primaryCTA={primaryCTA}
                />
            )}
            <div
                ref={wrapperRef}
                className={css(S.ProfessionalHeaderFixedWrapper)}
            >
                <div
                    ref={sentinelRef}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                    }}
                />
                <div className={css(S.ProfessionalHeaderContainer)}>
                    <div className={css(S.PageHeader)}>
                        <div ref={innerRef} className={css(S.PageHeaderInner)}>
                            <div className={css(S.LeftPane)}>
                                <SubRouteNavigation>
                                    {!LibraryLocations.some(
                                        (l) => !l.LocationHidden
                                    ) &&
                                        !hasLinkedLocation && (
                                            <RoccoButton
                                                color="secondary"
                                                asChild
                                            >
                                                <Link
                                                    to={
                                                        professionalPageWithEnquiry
                                                    }
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        openEnquiryModal(true);
                                                    }}
                                                    className="text-sm leading-6.5 tracking-0 md:text-base md:tracking-0"
                                                >
                                                    Contact
                                                </Link>
                                            </RoccoButton>
                                        )}
                                </SubRouteNavigation>
                            </div>
                            <div className={css(S.RightPane)}>
                                <div className={css(S.RightPaneInner)}>
                                    <div
                                        className={css(S.ProjectTitleContainer)}
                                    >
                                        <Header
                                            as="h1"
                                            className={css(S.ProjectTitle)}
                                            uc={true}
                                            dangerouslySetInnerHTML={{
                                                __html: iconAdjustedTitle,
                                            }}
                                        />

                                        <div
                                            className={css(S.ProfessionalTitle)}
                                        >
                                            {professionalCategory}
                                        </div>
                                    </div>
                                    <RightHeaderActionLinkDesktop
                                        primaryCTA={primaryCTA}
                                        isEnquiryOpen={enquiryModalOpen}
                                        onEnquiryModalOpenChange={
                                            handleEnquiryModalOpenChange
                                        }
                                        openEnquiryModal={openEnquiryModal}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
